import { create } from 'zustand'

export enum OnboardingType {
  PREVIEW_PAGE = 'preview-page',
  FILTER_BAR = 'filter-bar',
  REQUIREMENT_ASSESSMENT_PAGE = 'requirement-assessment-page',
  NEWSLETTERS_PAGE = 'newsletters-page',
  ADMINISTRATION = 'administration',
  USERS_PAGE = 'users-page',
  SELECT_LOCATION_PAGE = 'select-location-page',
}

interface OnboardingState {
  activeTypes: Record<OnboardingType, boolean>
  setActive: (type: OnboardingType, value: boolean) => void
  isActive: (type: OnboardingType) => boolean
  resetAll: (enabled?: boolean) => void
}

const STORAGE_KEY = 'onboardingCompleted'

const getInitialState = (): Record<OnboardingType, boolean> => {
  const stored = localStorage.getItem(STORAGE_KEY)
  const completed = stored ? JSON.parse(stored) : {}

  return {
    [OnboardingType.PREVIEW_PAGE]: !completed[OnboardingType.PREVIEW_PAGE],
    [OnboardingType.FILTER_BAR]: !completed[OnboardingType.FILTER_BAR],
    [OnboardingType.REQUIREMENT_ASSESSMENT_PAGE]:
      !completed[OnboardingType.REQUIREMENT_ASSESSMENT_PAGE],
    [OnboardingType.NEWSLETTERS_PAGE]:
      !completed[OnboardingType.NEWSLETTERS_PAGE],
    [OnboardingType.ADMINISTRATION]: !completed[OnboardingType.ADMINISTRATION],
    [OnboardingType.USERS_PAGE]: !completed[OnboardingType.USERS_PAGE],
    [OnboardingType.SELECT_LOCATION_PAGE]:
      !completed[OnboardingType.SELECT_LOCATION_PAGE],
  }
}
export const useOnboardingStore = create<
  OnboardingState & {
    toggleActive: (type: OnboardingType) => void
    isAnyActive: () => boolean
  }
>((set, get) => ({
  activeTypes: getInitialState(),

  setActive: (type, value) => {
    set((state) => ({
      activeTypes: {
        ...state.activeTypes,
        [type]: value,
      },
    }))

    if (!value && typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY)
      const completed = stored ? JSON.parse(stored) : {}
      completed[type] = true
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completed))
    }
  },

  isActive: (type) => get().activeTypes[type],

  isAnyActive: () => {
    const { activeTypes } = get()
    return Object.values(activeTypes).some((active) => active)
  },

  resetAll: (enabled = true) => {
    if (typeof window === 'undefined') return

    const newState = enabled
      ? {
          [OnboardingType.PREVIEW_PAGE]: true,
          [OnboardingType.FILTER_BAR]: true,
          [OnboardingType.REQUIREMENT_ASSESSMENT_PAGE]: true,
          [OnboardingType.NEWSLETTERS_PAGE]: true,
          [OnboardingType.ADMINISTRATION]: true,
          [OnboardingType.USERS_PAGE]: true,
          [OnboardingType.SELECT_LOCATION_PAGE]: true,
        }
      : {
          [OnboardingType.PREVIEW_PAGE]: false,
          [OnboardingType.FILTER_BAR]: false,
          [OnboardingType.REQUIREMENT_ASSESSMENT_PAGE]: false,
          [OnboardingType.NEWSLETTERS_PAGE]: false,
          [OnboardingType.ADMINISTRATION]: false,
          [OnboardingType.USERS_PAGE]: false,
          [OnboardingType.SELECT_LOCATION_PAGE]: false,
        }

    if (enabled) {
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          Object.fromEntries(Object.keys(newState).map((key) => [key, true]))
        )
      )
    }

    set({ activeTypes: newState })
  },

  toggleActive: (type) => {
    const current = get().isActive(type)
    get().setActive(type, !current)
  },
}))
